import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { cx } from '../cx'
import { Icon } from '../Icon'

export function Segmented({
  value,
  onChange,
  options,
  fill,
  className,
}: {
  value: string
  onChange: (v: string) => void
  options: Array<{ value: string; label: string }>
  fill?: boolean
  className?: string
}) {
  return (
    <div className={cx('seg', className)} style={fill ? { inlineSize: '100%' } : undefined} role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          className={cx(o.value === value && 'is-on')}
          style={fill ? { flex: 1 } : undefined}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function Tabs({
  value,
  onChange,
  items,
  className,
}: {
  value: string
  onChange: (v: string) => void
  items: Array<{ value: string; label: string; count?: number }>
  className?: string
}) {
  const strip = useRef<HTMLDivElement>(null)
  // Which edges hide a tab, so the strip can fade there on a phone.
  const [more, setMore] = useState('')

  useLayoutEffect(() => {
    const el = strip.current
    if (!el) return
    const measure = () => {
      // scrollLeft runs from 0 toward negative in a right to left strip.
      const gone = Math.abs(el.scrollLeft)
      const room = el.scrollWidth - el.clientWidth
      const next = [gone > 1 && 'start', room - gone > 1 && 'end'].filter(Boolean).join(' ')
      setMore((prev) => (prev === next ? prev : next))
    }
    measure()
    el.addEventListener('scroll', measure, { passive: true })
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    resize?.observe(el)
    return () => {
      el.removeEventListener('scroll', measure)
      resize?.disconnect()
    }
  }, [items.length])

  useEffect(() => {
    const el = strip.current
    const on = el?.querySelector<HTMLElement>('.is-on')
    if (!el || !on) return
    const box = el.getBoundingClientRect()
    const tab = on.getBoundingClientRect()
    if (tab.left < box.left || tab.right > box.right) on.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    // Counts arrive after the first paint and widen the strip, so this runs again then.
  }, [value, items.map((it) => it.count ?? '').join()])

  return (
    <div ref={strip} className={cx('tabs', className)} role="tablist" data-more={more || undefined}>
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          role="tab"
          aria-selected={it.value === value}
          className={cx(it.value === value && 'is-on')}
          onClick={() => onChange(it.value)}
        >
          {it.label}
          {/* A count is shown only when there is something to count. Keyed
              by its value, so a new count lands with a small pop. */}
          {it.count !== undefined && it.count > 0 && (
            <span key={it.count} className="badge badge-neutral tab-count" style={{ marginInlineStart: 6 }}>
              {faInt(it.count)}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

/**
 * Page buttons. A list that fits on one page has no pager, so nothing is
 * drawn for a single page. Where a count is printed beside it, use
 * `PagerBar`, which writes the count from the same numbers.
 */
export function Pager({
  page,
  pageCount,
  onChange,
  className,
}: {
  page: number
  pageCount: number
  onChange: (p: number) => void
  className?: string
}) {
  if (pageCount <= 1) return null
  const at = Math.min(Math.max(1, page), pageCount)
  const pages = pageWindow(at, pageCount)
  return (
    <nav className={cx('pager', className)} aria-label="صفحه‌بندی">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, at - 1))}
        disabled={at <= 1}
        aria-label="صفحه قبلی"
      >
        <Icon name="chevron-right" size="sm" />
      </button>
      {pages.map((p, i) =>
        p === null ? (
          <button key={`gap-${i}`} type="button" disabled aria-hidden="true">
            …
          </button>
        ) : (
          <button
            key={p}
            type="button"
            className={cx(p === at && 'is-on')}
            aria-current={p === at ? 'page' : undefined}
            onClick={() => onChange(p)}
          >
            {faInt(p)}
          </button>
        ),
      )}
      <button
        type="button"
        onClick={() => onChange(Math.min(pageCount, at + 1))}
        disabled={at >= pageCount}
        aria-label="صفحه بعدی"
      >
        <Icon name="chevron-left" size="sm" />
      </button>
    </nav>
  )
}

/** How many pages `total` rows fill at `pageSize` a page. Never less than one. */
export function pageCountOf(total: number, pageSize: number): number {
  if (pageSize <= 0) return 1
  return Math.max(1, Math.ceil(total / pageSize))
}

/**
 * The count beside a pager, written from the same three numbers the pager
 * is drawn from, so the two cannot disagree. Same wording as the table
 * footer: «۲۰ از ۱۴۲ تراکنش» while there are more pages (the rows on this
 * page, of all), «۱۴ تراکنش» when everything fits on one page, and nothing
 * for an empty list.
 */
export function pageRangeFa({
  page,
  pageSize,
  total,
  noun = 'ردیف',
}: {
  page: number
  pageSize: number
  total: number
  noun?: string
}): string {
  if (total <= 0) return ''
  const pages = pageCountOf(total, pageSize)
  if (pages <= 1) return `${faInt(total)} ${noun}`
  const at = Math.min(Math.max(1, page), pages)
  const shown = Math.min(pageSize, total - (at - 1) * pageSize)
  return `${faInt(shown)} از ${faInt(total)} ${noun}`
}

/**
 * The foot of a paged list: the count on the start, the pager on the end.
 * Both come from `page`, `pageSize` and `total`, which is the only way the
 * count and the pager are allowed to be drawn together.
 */
export function PagerBar({
  page,
  pageSize,
  total,
  onChange,
  noun,
  className,
}: {
  page: number
  pageSize: number
  total: number
  onChange: (p: number) => void
  noun?: string
  className?: string
}) {
  const text = pageRangeFa({ page, pageSize, total, noun })
  if (!text) return null
  return (
    <div className={cx('pager-bar', className)}>
      <span className="t-cap t-muted">{text}</span>
      <Pager page={page} pageCount={pageCountOf(total, pageSize)} onChange={onChange} />
    </div>
  )
}

/** At most seven slots: first, a window around the current page, last. */
function pageWindow(page: number, count: number): Array<number | null> {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1)
  const out: Array<number | null> = [1]
  const from = Math.max(2, page - 1)
  const to = Math.min(count - 1, page + 1)
  if (from > 2) out.push(null)
  for (let p = from; p <= to; p += 1) out.push(p)
  if (to < count - 1) out.push(null)
  out.push(count)
  return out
}

export interface StepSpec {
  label: string
}

/**
 * The stepper: a numbered circle and a title per step, nothing else. No
 * ordinal words («گام اول») and no «X از Y» line: the circles already say
 * where the person is, and the page heading is the current step's title.
 * A done step's circle carries a check, a failed one a cross.
 */
export function Steps({
  items,
  current,
  failedAt,
  className,
}: {
  items: StepSpec[]
  /** One based index of the active step. */
  current: number
  /** Paints that bullet danger. The rejected onboarding file uses it. */
  failedAt?: number
  className?: string
}) {
  return (
    <div className={cx('steps', className)} role="list">
      {items.map((s, i) => {
        const n = i + 1
        const failed = failedAt === n
        return (
          <div
            key={`${n}-${s.label}`}
            role="listitem"
            aria-current={n === current ? 'step' : undefined}
            className={cx('step', !failed && n < current && 'is-done', n === current && 'is-on')}
          >
            <span className={cx('bullet', failed && 'is-failed')} aria-hidden="true">
              {failed ? <Icon name="x" size="sm" /> : n < current ? <Icon name="check" size="sm" /> : faInt(n)}
            </span>
            <span className="cap">{s.label}</span>
          </div>
        )
      })}
    </div>
  )
}

export function Breadcrumb({
  items,
  className,
}: {
  items: Array<{ label: string; to?: string }>
  className?: string
}) {
  return (
    <nav className={cx('crumb', className)} aria-label="مسیر">
      {items.map((it, i) => (
        <span key={`${it.label}-${i}`} className="row" style={{ gap: 7 }}>
          {i > 0 && <Icon name="chevron-left" size="sm" />}
          {it.to && i < items.length - 1 ? (
            <a href={it.to}>{it.label}</a>
          ) : (
            <span className={i === items.length - 1 ? 'is-on' : undefined}>{it.label}</span>
          )}
        </span>
      ))}
    </nav>
  )
}

export function Fold({
  open,
  onToggle,
  title,
  meta,
  children,
}: {
  open: boolean
  onToggle: () => void
  title: string
  meta?: ReactNode
  children: ReactNode
}) {
  return (
    <>
      <button type="button" className="fold" aria-expanded={open} onClick={onToggle}>
        <Icon name="chevron-down" className="ic-fold" size="sm" />
        <span className="t-sm t-sb grow">{title}</span>
        {meta}
      </button>
      {open && <div style={{ paddingBlock: 'var(--s-4)' }}>{children}</div>}
    </>
  )
}

function faInt(n: number): string {
  return String(n).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
}
